import { useState } from 'react'
import { Link } from 'react-router-dom'
import { MapPin, Phone, Mail } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import api from '../../services/api'

export default function Footer() {
  const year = new Date().getFullYear()
  const { isAuthenticated } = useAuth()
  const [subState, setSubState] = useState('idle') // idle | loading | done | error

  const handleSubscribe = async () => {
    if (!isAuthenticated) {
      window.location.href = '/login'
      return
    }
    setSubState('loading')
    try {
      await api.post('/customer/subscribe')
      setSubState('done')
    } catch {
      setSubState('error')
    }
  }

  return (
    <footer className="bg-gray-900 dark:bg-black text-gray-300 mt-auto">
      {/* Newsletter strip */}
      <div className="bg-primary py-10 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-white text-xl">Get exclusive deals!</h3>
            <p className="text-red-100 text-sm mt-1">Subscribe for special offers, new arrivals and bulk pricing.</p>
          </div>
          <div>
            {subState === 'done' ? (
              <div className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-white/20 text-white text-sm font-medium">
                ✅ You're subscribed!
              </div>
            ) : (
              <button
                onClick={handleSubscribe}
                disabled={subState === 'loading'}
                className="btn-gold px-8 py-2.5 text-sm flex items-center gap-2"
              >
                {subState === 'loading'
                  ? <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Subscribing…</>
                  : subState === 'error' ? '⚠ Try again'
                  : '🔔 Subscribe to Deals'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 py-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        {/* Brand */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <img src="/logo.text-w.png" alt="Tony's Liquor" className="h-10 w-auto object-contain" />
            <div className="hidden">
              <div className="text-gray-500 text-xs font-display tracking-widest uppercase">Distributor</div>
            </div>
          </div>
          <p className="text-sm leading-relaxed text-gray-400 mb-4">
            Your trusted liquor distributor in Pretoria. Thousands of products, bulk pricing, and fast delivery.
          </p>
          {/* No Underage Drinking — aware.org.za */}
          <a
            href="https://www.aware.org.za"
            target="_blank"
            rel="noopener noreferrer"
            title="No alcohol may be sold to persons under the age of 18 — aware.org.za"
          >
            <img
              src="/aware-logo.webp"
              alt="No Underage Drinking — aware.org.za"
              className="h-14 w-auto object-contain opacity-90 hover:opacity-100 transition-opacity"
            />
          </a>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="text-white font-display font-semibold mb-4">Quick Links</h4>
          <ul className="space-y-2.5 text-sm">
            {[
              { to: '/shop', label: 'All Products' },
              { to: '/shop?category=spirits', label: 'Spirits & Whiskey' },
              { to: '/shop?category=wine', label: 'Wine' },
              { to: '/shop?category=beer', label: 'Beer & Cider' },
              { to: '/cart', label: 'My Cart' },
              { to: '/wishlist', label: 'Wishlist' },
            ].map(link => (
              <li key={link.to}>
                <Link to={link.to} className="hover:text-gold hover:pl-1 transition-all">
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Account */}
        <div>
          <h4 className="text-white font-display font-semibold mb-4">My Account</h4>
          <ul className="space-y-2.5 text-sm">
            {[
              { to: '/login', label: 'Login' },
              { to: '/account', label: 'My Profile' },
              { to: '/orders', label: 'Order History' },
              { to: '/wishlist', label: 'Wishlist' },
              { to: '/contact', label: 'Contact Us' },
            ].map(link => (
              <li key={link.to}>
                <Link to={link.to} className="hover:text-gold hover:pl-1 transition-all">
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4 className="text-white font-display font-semibold mb-4">Contact</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex gap-3">
              <MapPin size={16} className="text-primary mt-0.5 flex-shrink-0" />
              <span>386 Johannes Ramkhoase St<br />Pretoria Central, Gauteng</span>
            </li>
            <li className="flex gap-3">
              <Phone size={16} className="text-primary flex-shrink-0" />
              <a href="tel:0123231520" className="hover:text-gold transition-colors">012 323 1520</a>
            </li>
            <li className="flex gap-3">
              <Mail size={16} className="text-primary flex-shrink-0" />
              <a href="mailto:info@tonysliquor.co.za" className="hover:text-gold transition-colors">
                info@tonysliquor.co.za
              </a>
            </li>
          </ul>
          <div className="mt-5 p-3 bg-white/5 rounded-lg text-xs text-gray-400">
            <div className="font-semibold text-gray-300 mb-1">Business Hours</div>
            <div>Mon–Fri: 08:00 – 18:00</div>
            <div>Sat: 08:00 – 15:00</div>
            <div>Sun: Closed</div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/5 py-5 px-4 text-center text-xs text-gray-600">
        <p>© {year} Tony's Liquor Distributor. All rights reserved. | 
          <Link to="/privacy" className="hover:text-gray-400 ml-1">Privacy Policy</Link> |
          <Link to="/terms" className="hover:text-gray-400 ml-1">Terms of Service</Link>
        </p>
        <p className="mt-1 text-gray-700">Drink responsibly. Not for persons under 18. E&amp;OE.</p>
      </div>
    </footer>
  )
}
